import { nanoid } from "nanoid";
import React from "react";

import { type Scope, untrack } from "~/tools/impulse";
import type { GetImpulseFormOutput, ImpulseForm } from "~/tools/impulse-form";
import { useHandler } from "~/tools/use-handler";

import { FormContextProvider, type FormContextValue } from "./_context";

export interface FormProps<TForm extends ImpulseForm> extends Omit<React.HTMLAttributes<HTMLFormElement>, "onSubmit"> {
	state: undefined | TForm;
	onSubmit?: (values: GetImpulseFormOutput<TForm>, scope: Scope) => void | Promise<unknown>;
}

/**
 * A form that is powered by Impulse Form.
 */
export const Form = React.forwardRef(
	({ state, id, onSubmit, onReset, ...props }, ref: React.ForwardedRef<HTMLFormElement>) => {
		const context = React.useMemo<FormContextValue>(() => ({ id: id ?? nanoid() }), [id]);
		const onSubmitStable = useHandler(onSubmit);

		React.useEffect(() => {
			if (onSubmitStable != null) {
				return state?.onSubmit((output) => untrack((scope) => onSubmitStable(output, scope)));
			}
		}, [state, onSubmitStable]);

		return (
			<FormContextProvider value={context}>
				<form
					id={context.id}
					noValidate
					onReset={(event) => {
						event.preventDefault();
						state?.reset();
						onReset?.(event);
					}}
					onSubmit={(event) => {
						event.preventDefault();
						void state?.submit();
					}}
					ref={ref}
					{...props}
				/>
			</FormContextProvider>
		);
	},
) as {
	<TForm extends ImpulseForm>(props: FormProps<TForm>, ref: React.ForwardedRef<HTMLFormElement>): React.ReactElement;

	displayName?: string;
};

if (import.meta.env.DEV) {
	Form.displayName = "Form";
}
